import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert, Unlink } from 'lucide-react';
import { Pill } from '@/components/widgets/Pill';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { adminMainAccount } from '@/api/admin';
import { fmtPnLNumber, fmtTime } from '@/lib/format';
import { ApiError } from '@/lib/http';
import { cn } from '@/lib/utils';
import { useLiveQuote, usePriceFlash } from '@/realtime/use-quotes';
import type { UserVirtualPosition } from '@/api/types';
import { computePositionLive } from './position-pnl';

/** 价格跳动时的淡色高亮背景(emerald/red,约等于设计 token 的 up/down 色) */
const flashBg = (f: 'up' | 'down' | null): string =>
  f === 'up' ? 'rgba(16,185,129,0.18)' : f === 'down' ? 'rgba(239,68,68,0.18)' : 'transparent';

/**
 * 各用户虚拟持仓表 — 与主账户净仓表并列(§9.4)。
 *
 * v3 主账户真实层是按品种净仓(net_positions,无单一 user_id),"谁持什么仓"看用户层真相源
 * virtual_legs,每行带明确用户ID。现价 / 浮动 PnL 由每行订阅实时行情(quote WS)本地计算,逐 tick
 * 跳动(用户视角口径,与主账户持仓表同 computePositionLive:user_side/virtual_volume/entry_mark)。
 */
export function UserPositionsTable({ positions }: { positions: UserVirtualPosition[] }) {
  const qc = useQueryClient();
  const [target, setTarget] = useState<UserVirtualPosition | null>(null);
  const [detachTarget, setDetachTarget] = useState<UserVirtualPosition | null>(null);
  const [reason, setReason] = useState('');

  const openClose = (p: UserVirtualPosition) => {
    setReason('');
    setTarget(p);
  };
  const closeDialog = () => {
    setTarget(null);
    setReason('');
  };

  const closePosition = useMutation({
    mutationFn: (p: UserVirtualPosition) =>
      adminMainAccount.closeUserPosition(p.legId, {
        reason: reason.trim() || '资金账户大屏仓位风控处置',
      }),
    onSuccess: (res) => {
      toast.success('已触发仓位风控平仓', {
        description: `用户 ${res.userId} · 仓位 ${res.positionId} · ${res.status}`,
      });
      closeDialog();
      void qc.invalidateQueries({ queryKey: ['admin', 'user-positions'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'main-account', 'positions'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'real-account', 'capacity'] });
    },
    onError: (err) => {
      toast.error('平仓失败', {
        description: err instanceof ApiError ? err.message : '请稍后重试',
      });
    },
  });
  const busy = closePosition.isPending;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>各用户虚拟持仓 · {positions.length} 笔</span>
            <span className="text-xs font-normal text-ink-3">用户层(virtual_legs);主账户真实净仓见上表</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg-3 text-ink-2 text-xs">
                <tr>
                  <th className="px-3 py-2 text-left font-normal">用户ID</th>
                  <th className="px-3 py-2 text-left font-normal">品种</th>
                  <th className="px-3 py-2 text-left font-normal">方向</th>
                  <th className="px-3 py-2 text-right font-normal">手数</th>
                  <th className="px-3 py-2 text-right font-normal">入场价</th>
                  <th className="px-3 py-2 text-right font-normal">当前价</th>
                  <th className="px-3 py-2 text-right font-normal">浮动 PnL</th>
                  <th className="px-3 py-2 text-left font-normal">开仓时间</th>
                  <th className="px-3 py-2 text-left font-normal">状态</th>
                  <th className="px-3 py-2 text-right font-normal">操作</th>
                </tr>
              </thead>
              <tbody>
                {positions.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-3 py-8 text-center text-ink-3">
                      暂无用户持仓
                    </td>
                  </tr>
                )}
                {positions.map((p) => (
                  <UserPositionRow
                    key={p.legId}
                    p={p}
                    onClose={openClose}
                    onDetach={setDetachTarget}
                    disabled={busy}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={target != null} onOpenChange={(v) => !v && !busy && closeDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-warn">
              <ShieldAlert className="h-5 w-5" />
              仓位风控平仓
            </DialogTitle>
            <DialogDescription>
              {target?.detached
                ? // 脱钩腿:不再计入真实净仓目标,平仓走中价结算、不向真实账户发单。
                  // 若真实账户还有该品种的残留仓,不会被这次平仓带走,需另行处置。
                  '该持仓已脱钩:将按仓位风控规则关闭该用户虚拟持仓,按当前报价中价结算,不会向真实账户发单、不会平掉主账户净仓。此操作不可逆。'
                : '将按仓位风控规则关闭该用户虚拟持仓,并同步主账户净仓。此操作不可逆。'}
              用户端只显示系统风控结算提示,不显示后台操作来源。
            </DialogDescription>
          </DialogHeader>
          {target && (
            <div className="rounded-md border border-line bg-bg-2 p-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <KV label="用户ID" value={target.userId} />
                <KV label="仓位ID" value={target.legId} />
                <KV label="品种" value={target.symbolId} />
                <KV label="方向" value={target.side} />
                <KV label="手数" value={(target.volume / 100).toFixed(2)} />
                <KV label="状态" value={target.status} />
                <KV label="对冲" value={target.detached ? '已脱钩(不发真实单)' : '对冲中(同步真实净仓)'} />
              </div>
              <div className="mt-3 text-xs text-ink-3">
                用户提示:因触发仓位风控规则,系统已自动完成本次仓位结算
              </div>
            </div>
          )}
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="close-pos-reason">操作原因(可选,仅内部审计)</Label>
              <Input
                id="close-pos-reason"
                placeholder="例如:人工核账后按风控处置"
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 256))}
                disabled={busy}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={busy}>取消</Button>
            <Button
              variant="warn"
              onClick={() => target && closePosition.mutate(target)}
              disabled={!target || busy}
            >
              <ShieldAlert className="h-4 w-4" />
              {busy ? '处理中' : '确认平仓'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DetachDialog target={detachTarget} onClose={() => setDetachTarget(null)} />
    </>
  );
}

/** 单行用户虚拟持仓 — 订阅自己品种的实时行情,逐 tick 算现价/浮盈并闪烁。 */
function UserPositionRow({
  p,
  onClose,
  onDetach,
  disabled,
}: {
  p: UserVirtualPosition;
  onClose: (p: UserVirtualPosition) => void;
  onDetach: (p: UserVirtualPosition) => void;
  disabled: boolean;
}) {
  const quote = useLiveQuote(p.symbolId);
  const { currentPrice, unrealizedPnl } = computePositionLive(
    { side: p.side, volume: p.volume, entryPrice: p.entryPrice },
    quote,
  );
  const flash = usePriceFlash(currentPrice);

  return (
    <tr className="border-t border-line row-hover">
      <td className="px-3 py-2 num text-ink-1">{p.userId}</td>
      <td className="px-3 py-2 num text-ink-2">{p.symbolId}</td>
      <td className="px-3 py-2">
        <Pill tone={p.side === 'BUY' ? 'up' : 'down'}>{p.side}</Pill>
      </td>
      <td className="px-3 py-2 text-right num">{(p.volume / 100).toFixed(2)}</td>
      <td className="px-3 py-2 text-right num">{p.entryPrice.toFixed(2)}</td>
      <td
        className="px-3 py-2 text-right num font-medium"
        style={{ backgroundColor: flashBg(flash), transition: 'background-color 300ms ease-out' }}
      >
        {currentPrice != null ? currentPrice.toFixed(2) : '—'}
      </td>
      {unrealizedPnl != null ? (
        <td className={cn('px-3 py-2 text-right num font-medium', unrealizedPnl >= 0 ? 'text-up' : 'text-down')}>
          {fmtPnLNumber(unrealizedPnl)}
        </td>
      ) : (
        <td className="px-3 py-2 text-right text-xs text-ink-3">实时</td>
      )}
      <td className="px-3 py-2 num text-xs text-ink-2">{fmtTime(p.openedAt)}</td>
      <td className="px-3 py-2 text-xs text-ink-2">
        <span className="inline-flex items-center gap-1.5">
          {p.status}
          {p.detached && (
            <Pill tone="warn" title="已脱钩:不计入真实净仓目标,平仓不发真实单、按中价结算">
              已脱钩
            </Pill>
          )}
        </span>
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex items-center justify-end gap-1.5">
          {/* 只平真实仓(逐笔脱钩):已脱钩的腿不再显示,避免重复操作(后端也会 409)。 */}
          {!p.detached && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onDetach(p)}
              disabled={disabled}
              title="只平该用户对应的真实敞口,不动他的虚拟持仓(不可逆)"
            >
              <Unlink className="h-4 w-4" />
              只平真实仓
            </Button>
          )}
          <Button
            type="button"
            variant="warn"
            size="sm"
            onClick={() => onClose(p)}
            disabled={disabled}
            title="按仓位风控规则平仓"
          >
            <ShieldAlert className="h-4 w-4" />
            平仓
          </Button>
        </div>
      </td>
    </tr>
  );
}

/**
 * 逐笔脱钩确认弹窗:打开即拉**只读预览**,把真实层将要发生的动作摆在管理员面前再让他确认。
 *
 * 为什么非要预览:v3 真实账户是全平台每品种一个净仓。摘掉一个人后按剩下的人重新轧净,
 * 动作可能是平仓/减仓,**也可能是反向开仓** —— 不预告的话,管理员按下「只平真实仓」却看到
 * 系统开了一笔单,会当成 bug。
 */
function DetachDialog({
  target,
  onClose,
}: {
  target: UserVirtualPosition | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (target) {
      setReason('');
    }
  }, [target]);

  const preview = useQuery({
    queryKey: ['admin', 'user-positions', 'detach-preview', target?.legId],
    queryFn: () => adminMainAccount.detachPreview(target!.legId),
    enabled: !!target,
    // 净仓随行情/他人下单变化,每次打开都重新取,不吃缓存(预览过期会误导决策)。
    staleTime: 0,
    gcTime: 0,
  });

  const run = useMutation({
    mutationFn: () =>
      adminMainAccount.detachUserPosition(target!.legId, {
        reason: reason.trim(),
      }),
    onSuccess: (res) => {
      if (res.converged) {
        toast.success('已脱钩', { description: res.actionText ?? '真实层已按新目标收敛' });
      } else {
        // converged=false ≠ 失败:标记已生效、真实层仍在收敛(FLIP 分两拍 / 回执在途)。
        // 措辞不能吓人也不能报成功,如实说「正在收敛」并让管理员看一眼大屏。
        toast.warning('已脱钩,真实层仍在收敛', {
          description: res.warning ?? '30 秒净额对账会继续完成,请关注大屏',
        });
      }
      onClose();
      void qc.invalidateQueries({ queryKey: ['admin', 'user-positions'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'main-account', 'positions'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'real-account', 'capacity'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    },
    onError: (err) => {
      toast.error('脱钩失败', { description: err instanceof ApiError ? err.message : '请稍后重试' });
    },
  });

  const busy = run.isPending;
  const reasonOk = reason.trim().length >= 4;
  const pv = preview.data;
  // 反向开仓是最需要突出的一支:与「平仓」的直觉完全相反。
  const isOpen = pv?.action === 'OPEN' || pv?.action === 'FLIP';

  return (
    <Dialog open={target != null} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-down">
            <Unlink className="h-5 w-5" />
            只平真实仓(逐笔脱钩)
          </DialogTitle>
          <DialogDescription>
            把这个用户的<strong>这一笔持仓</strong>摘出对冲池。他的 App 持仓照常显示、照常浮动、
            照常能自己平 —— <strong>用户端完全无感</strong>;但此后这笔仓<strong>没有真实对冲支撑</strong>,
            盈亏由平台全额承担。<strong>不可逆</strong>。
          </DialogDescription>
        </DialogHeader>

        {target && (
          <div className="rounded-md border border-line bg-bg-2 p-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <KV label="用户ID" value={target.userId} />
              <KV label="仓位ID" value={target.legId} />
              <KV label="品种" value={pv?.symbolName ?? target.symbolId} />
              <KV label="用户方向 / 手数" value={`${target.side} ${(target.volume / 100).toFixed(2)}`} />
            </div>
          </div>
        )}

        {/* 真实层动作预览 */}
        <div
          className={cn(
            'rounded-md border p-3 text-sm',
            isOpen ? 'border-down/50 bg-down/5' : 'border-line bg-bg-2',
          )}
        >
          <div className="text-xs text-ink-3 mb-1">真实账户将执行</div>
          {preview.isLoading && <div className="text-ink-3">推演中…</div>}
          {preview.isError && (
            <div className="text-down">
              预览失败:{preview.error instanceof ApiError ? preview.error.message : '请重试'}
            </div>
          )}
          {pv && (
            <>
              <div className={cn('font-medium', isOpen ? 'text-down' : 'text-ink-0')}>
                {pv.actionText ?? pv.action}
              </div>
              {isOpen && (
                <div className="mt-1.5 text-xs leading-relaxed text-down">
                  注意:这不是平仓。真实账户每品种只有<strong>一笔净仓</strong>,把这个用户摘出去后,
                  系统要按<strong>剩下的用户</strong>重新对冲,因此反而需要开仓。
                </div>
              )}
              <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                <KV label="当前真实净仓" value={netText(pv.currentNet)} />
                <KV label="脱钩前目标" value={netText(pv.targetBefore)} />
                <KV label="脱钩后目标" value={netText(pv.targetAfter)} />
              </div>
            </>
          )}
        </div>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="detach-reason">操作原因(至少 4 字,写审计)</Label>
            <Input
              id="detach-reason"
              placeholder="例如:该用户敞口过大,人工解除对冲"
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 256))}
              disabled={busy}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            取消
          </Button>
          <Button
            variant="destructive"
            onClick={() => run.mutate()}
            disabled={busy || !reasonOk || preview.isLoading || pv?.detached === true}
          >
            <Unlink className="h-4 w-4" />
            {busy ? '处理中' : '确认脱钩'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** 带符号 Lots100 → 「0.02 手 净多 / 净空 / 无持仓」。 */
function netText(signed: number): string {
  if (signed === 0) return '无持仓';
  return `${(Math.abs(signed) / 100).toFixed(2)} 手 ${signed > 0 ? '净多' : '净空'}`;
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-ink-3">{label}</div>
      <div className="num text-ink-1">{value}</div>
    </div>
  );
}
